/**
 * File Upload Utility - Platform Client Utils
 *
 * Reusable utility for uploading files to the server.
 * Handles FormData creation, HTTP requests, and error handling.
 */

import logger from '@ayocore/platform/common/helpers/logger';

// Get API base URL from environment variables (same pattern as fetcher.ts)
const getApiBaseUrl = (): string => {
  const strip = (s: string) => (s === '' ? '' : s.replace(/\/$/, ''));

  // Browser environment (Vite)
  if (
    typeof import.meta !== 'undefined' &&
    (import.meta as unknown as { env?: Record<string, string | boolean> }).env
  ) {
    const env = (import.meta as unknown as { env?: Record<string, string | boolean> }).env || {};
    if (env.MODE === 'production') return '';
    if (env.VITE_API_BASE_URL !== undefined) return strip(String(env.VITE_API_BASE_URL));
    const host = typeof env.VITE_SERVER_HOST === 'string' ? env.VITE_SERVER_HOST : 'localhost';
    const port = typeof env.VITE_SERVER_PORT === 'string' ? env.VITE_SERVER_PORT : '5000';
    return `http://${host}:${port}`;
  }

  // Node.js environment (server-side)
  if (typeof process !== 'undefined' && process.env) {
    if (process.env.NODE_ENV === 'production') return '';
    if (process.env.VITE_API_BASE_URL !== undefined)
      return strip(String(process.env.VITE_API_BASE_URL));
    const host = process.env.SERVER_HOST || 'localhost';
    const port = process.env.SERVER_PORT || '5000';
    return `http://${host}:${port}`;
  }

  // Fallback
  return 'http://localhost:5000';
};

/**
 * Параметры пережатия фото на сервере при загрузке (растровые изображения).
 * Сервер ресайзит по макс. стороне `width` (fit inside, без увеличения) и
 * кодирует в JPEG с качеством `quality`. Оригинал не сохраняется.
 */
export interface ImageProcessingOptions {
  /** Макс. сторона результата в px. */
  width?: number;
  /** Качество JPEG 1-100. */
  quality?: number;
}

export interface UploadOptions {
  /** Optional directory to organize files (e.g., 'contracts/templates') */
  directory?: string;
  /** Progress callback for upload progress tracking */
  onProgress?: (progress: number) => void;
  /** Custom upload endpoint (defaults to '/upload') */
  endpoint?: string;
  /** Пережатие фото на сервере (опц.) — только для растровых изображений. */
  imageProcessing?: ImageProcessingOptions;
  /**
   * Папка Диска: сервер размещает файл в ней В ЭТОМ ЖЕ запросе единым
   * протоколом переноса (блокировки + доменные правила области) и отвечает
   * успехом только после коммита; при сбое компенсирует и запись, и storage.
   * Отдельный RPC смены folder_id после загрузки не нужен и запрещён.
   */
  driveFolderId?: number;
  /**
   * Таймаут запроса, мс. По умолчанию 30000; `0` — без таймаута (большие
   * файлы на медленном канале — например ZIP-архивы для распаковки на Диск).
   */
  timeoutMs?: number;
}

export interface UploadResult {
  success: boolean;
  file?: {
    id: number;
    filename: string;
    originalName: string;
    filePath: string;
    fileSize: number;
    mimeType: string;
    directory?: string;
    created_at: string;
    publicUrl?: string;
  };
  error?: string;
}

/**
 * Upload a file to the server
 *
 * @param file - File object to upload
 * @param options - Upload configuration options
 * @returns Promise with upload result
 *
 * @example
 * ```typescript
 * const result = await uploadFile(selectedFile, {
 *   directory: 'contracts/templates',
 *   onProgress: (progress) => logger.info(`${progress}%`)
 * });
 *
 * if (result.success) {
 *   logger.info('File uploaded:', result.file?.filename);
 * } else {
 *   logger.error('Upload failed:', result.error);
 * }
 * ```
 */
export async function uploadFile(file: File, options: UploadOptions = {}): Promise<UploadResult> {
  const { directory, onProgress, endpoint = '/upload', imageProcessing, driveFolderId } = options;

  try {
    // Validate file
    if (!file) {
      return {
        success: false,
        error: 'No file provided',
      };
    }

    // Create FormData
    const formData = new FormData();
    formData.append('file', file);

    // Add directory if specified
    if (directory?.trim()) {
      formData.append('dir', directory.trim());
    }

    // Пережатие фото на сервере (только если заданы параметры) — JSON-поле,
    // сервер разбирает его в processUploadedFile.
    if (imageProcessing && (imageProcessing.width != null || imageProcessing.quality != null)) {
      formData.append('image_processing', JSON.stringify(imageProcessing));
    }

    // Upload-to-folder: размещение в папке Диска в этом же запросе (см. UploadOptions).
    if (driveFolderId != null) {
      formData.append('drive_folder_id', String(driveFolderId));
    }

    // Construct full URL using backend configuration
    const apiBaseUrl = getApiBaseUrl();
    // Ensure no double slashes by removing trailing slash from base URL and leading slash from endpoint
    const cleanBaseUrl = apiBaseUrl.replace(/\/$/, ''); // Remove trailing slash
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`; // Ensure leading slash
    const fullUrl = `${cleanBaseUrl}${cleanEndpoint}`;

    logger.info('📤 [UploadFile] Uploading to:', fullUrl);

    // Create XMLHttpRequest for progress tracking
    return new Promise<UploadResult>(resolve => {
      const xhr = new XMLHttpRequest();

      // Progress tracking
      if (onProgress) {
        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        });
      }

      // Handle response
      xhr.addEventListener('load', () => {
        try {
          const result: unknown = JSON.parse(xhr.responseText);
          const data = result as UploadResult;

          if (xhr.status === 200 && data.success) {
            logger.info('✅ [UploadFile] Upload successful:', data.file);
            resolve({
              success: true,
              file: data.file,
            });
          } else {
            logger.error(
              '❌ [UploadFile] Upload failed:',
              (data && (data as UploadResult).error) || `HTTP ${xhr.status}`
            );
            resolve({
              success: false,
              error: (data as UploadResult).error || `HTTP ${xhr.status}: ${xhr.statusText}`,
            });
          }
        } catch (parseError) {
          logger.error('❌ [UploadFile] Failed to parse response:', parseError);
          resolve({
            success: false,
            error: 'Failed to parse server response',
          });
        }
      });

      // Handle network errors
      xhr.addEventListener('error', () => {
        logger.error('❌ [UploadFile] Network error occurred');
        resolve({
          success: false,
          error: 'Network error occurred during upload',
        });
      });

      // Handle timeout
      xhr.addEventListener('timeout', () => {
        logger.error('❌ [UploadFile] Upload timeout');
        resolve({
          success: false,
          error: 'Upload timeout - file may be too large',
        });
      });

      // Configure and send request
      xhr.timeout = options.timeoutMs ?? 30000; // 0 = без таймаута
      xhr.open('POST', fullUrl);
      xhr.withCredentials = true;
      xhr.send(formData);
    });
  } catch (error) {
    logger.error('❌ [UploadFile] Unexpected error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown upload error',
    };
  }
}

/**
 * Upload multiple files sequentially
 *
 * @param files - Array of files to upload
 * @param options - Upload configuration options
 * @returns Promise with array of upload results
 */
export async function uploadFiles(
  files: File[],
  options: UploadOptions = {}
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    // Create progress callback for individual file
    const fileProgress = options.onProgress
      ? (progress: number) => {
          const overallProgress = (i / files.length) * 100 + progress / files.length;
          const cb = options.onProgress;
          if (cb) cb(Math.round(overallProgress));
        }
      : undefined;

    const result = await uploadFile(file, {
      ...options,
      onProgress: fileProgress,
    });

    results.push(result);

    // Stop on first error (optional - could continue)
    if (!result.success) {
      break;
    }
  }

  return results;
}

/**
 * Validate file before upload
 *
 * @param file - File to validate
 * @param options - Validation options
 * @returns Validation result
 */
export interface FileValidationOptions {
  maxSize?: number; // in bytes
  allowedTypes?: string[]; // MIME types
  allowedExtensions?: string[]; // file extensions
}

export function validateFile(
  file: File,
  options: FileValidationOptions = {}
): { valid: boolean; error?: string } {
  const { maxSize, allowedTypes, allowedExtensions } = options;

  // Check file size
  if (maxSize && file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit`,
    };
  }

  // Check MIME type
  if (allowedTypes && !allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `File type ${file.type} is not allowed`,
    };
  }

  // Check file extension
  if (allowedExtensions) {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !allowedExtensions.includes(extension)) {
      return {
        valid: false,
        error: `File extension .${extension} is not allowed`,
      };
    }
  }

  return { valid: true };
}
